import { useState } from "react";
import { api, type Named, type Role } from "../lib/api.js";
import { Notice, Tabs, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

interface SettingsResponse {
  settings: Record<string, string>;
  server: { id_server: string; relay_server: string; api_server: string; key: string };
}

interface OidcProvider {
  id: string; name: string; display_name: string; issuer: string;
  client_id: string; client_secret: string; scopes: string; username_claim: string;
  enabled: boolean; auto_register: boolean;
}

interface LdapConfig {
  enabled: boolean; url: string; bindDn: string; bindPassword: string;
  searchBase: string; searchFilter: string; usernameAttribute: string;
  displayNameAttribute: string; mailAttribute: string; userDnTemplate: string;
  autoRegister: boolean; tlsRejectUnauthorized: boolean;
}

export default function Settings({ onToggleRail }: PageProps) {
  const [tab, setTab] = useState<"server" | "roles" | "sso" | "ldap">("server");
  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title"><h1>Server</h1></div>
      </header>
      <div className="content">
        <Tabs
          active={tab} onChange={setTab}
          tabs={[
            { id: "server", label: "Addresses" },
            { id: "roles", label: "Roles" },
            { id: "sso", label: "Single sign-on" },
            { id: "ldap", label: "LDAP" },
          ]}
        />
        {tab === "server" ? <ServerTab /> : null}
        {tab === "roles" ? <RolesTab /> : null}
        {tab === "sso" ? <SsoTab /> : null}
        {tab === "ldap" ? <LdapTab /> : null}
      </div>
    </>
  );
}

function ServerTab() {
  const data = useAsync(() => api.get<SettingsResponse>("/api/console/settings"), []);
  const [form, setForm] = useState<Record<string, string> | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const values = form ?? {
    id_server: data.data?.settings.id_server ?? data.data?.server.id_server ?? "",
    relay_server: data.data?.settings.relay_server ?? data.data?.server.relay_server ?? "",
    api_server: data.data?.settings.api_server ?? data.data?.server.api_server ?? "",
    server_key: data.data?.settings.server_key ?? data.data?.server.key ?? "",
    require_login: data.data?.settings.require_login ?? "0",
  };

  const set = (k: string, v: string) => setForm({ ...values, [k]: v });

  async function save() {
    setError(""); setSaved(false);
    try { await api.post("/api/console/settings", values); setSaved(true); data.reload(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      {error ? <Notice>{error}</Notice> : null}
      {saved ? <Notice kind="ok">Saved. New installers will use these addresses.</Notice> : null}

      <div className="panel">
        <div className="panel__head"><h2>Addresses handed to clients</h2></div>
        <div className="panel__body">
          <p className="faint small">
            These go into generated installers and into the config string people can paste
            into an existing client. Use the address clients reach from outside your network.
          </p>
          <div className="field">
            <label className="field__label">ID server</label>
            <input className="mono" value={values.id_server} onChange={(e) => set("id_server", e.target.value)}
              placeholder="rustdesk.example.com" />
            <span className="field__hint">Hostname or IP of this rendezvous server. Port 21116 is assumed.</span>
          </div>
          <div className="field">
            <label className="field__label">Relay server</label>
            <input className="mono" value={values.relay_server} onChange={(e) => set("relay_server", e.target.value)}
              placeholder="rustdesk.example.com" />
            <span className="field__hint">Leave blank to use the ID server address.</span>
          </div>
          <div className="field">
            <label className="field__label">API and console</label>
            <input className="mono" value={values.api_server} onChange={(e) => set("api_server", e.target.value)}
              placeholder="https://rustdesk.example.com" />
            <span className="field__hint">
              Full URL including scheme. Install scripts download from here, so it must be
              reachable from the machines you deploy to.
            </span>
          </div>
          <div className="field">
            <label className="field__label">Server key</label>
            <input className="mono" value={values.server_key} onChange={(e) => set("server_key", e.target.value)} />
            <span className="field__hint">
              The public key printed by the rendezvous server at startup. Clients without it
              are refused, and sessions cannot be encrypted without it.
            </span>
          </div>
          <label className="check">
            <input type="checkbox" checked={values.require_login === "1"}
              onChange={(e) => set("require_login", e.target.checked ? "1" : "0")} />
            Require a signed-in account for every connection
          </label>
          <p className="small faint">
            With this off, someone who knows a machine&rsquo;s ID and password can connect without
            an account here, and permission rules will not apply to them.
          </p>
          <button className="btn btn--primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

function RolesTab() {
  const roles = useAsync(() => api.get<Role[]>("/api/console/roles"), []);
  const perms = useAsync(
    () => api.get<{ console: string[]; control: string[] }>("/api/console/roles/available-permissions"),
    [],
  );
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState("");

  async function create() {
    setError("");
    try {
      await api.post("/api/console/roles", { name, permissions: selected });
      setName(""); setSelected([]); roles.reload();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      {error ? <Notice>{error}</Notice> : null}
      <div className="panel">
        <div className="panel__head"><h2>Roles</h2></div>
        <div className="panel__body" style={{ padding: 0 }}>
          <p className="faint small" style={{ padding: "0.75rem 1rem 0" }}>
            A role decides what someone can do in this console. What they can do on a
            machine is set under Permissions.
          </p>
          <table className="data">
            <thead><tr><th>Role</th><th>Can do</th><th style={{ width: 80 }} /></tr></thead>
            <tbody>
              {(roles.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.name}
                    {r.built_in ? <span className="tag" style={{ marginLeft: 8 }}>Built in</span> : null}
                    {r.note ? <div className="small faint">{r.note}</div> : null}
                  </td>
                  <td className="muted small">
                    {r.permissions.includes("*") ? "Everything" : `${r.permissions.length} permissions`}
                  </td>
                  <td className="actions">
                    {!r.built_in ? (
                      <button className="btn btn--danger btn--sm"
                        onClick={() => api.del(`/api/console/roles/${r.id}`).then(roles.reload)}>
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><h2>New role</h2></div>
        <div className="panel__body">
          <div className="field">
            <label className="field__label">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Helpdesk" />
          </div>
          <div style={{ columns: 2, columnGap: "1.5rem" }}>
            {(perms.data?.console ?? []).filter((p) => p !== "*").map((p) => (
              <label className="check" key={p} style={{ breakInside: "avoid" }}>
                <input type="checkbox" checked={selected.includes(p)}
                  onChange={() => setSelected((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p])} />
                <code className="mono small">{p}</code>
              </label>
            ))}
          </div>
          <button className="btn btn--primary" style={{ marginTop: "0.75rem" }}
            onClick={create} disabled={!name.trim()}>
            Create role
          </button>
        </div>
      </div>
    </div>
  );
}

function SsoTab() {
  const list = useAsync(() => api.get<OidcProvider[]>("/api/console/oidc-providers"), []);
  const roles = useAsync(() => api.get<Role[]>("/api/console/roles"), []);
  const groups = useAsync(() => api.get<Named[]>("/api/console/groups"), []);
  const [form, setForm] = useState<Partial<OidcProvider> & { default_role_id?: string; default_group_id?: string }>({
    scopes: "openid profile email", username_claim: "preferred_username",
    enabled: true, auto_register: true,
  });
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  async function create() {
    setError(""); setSaved(false);
    try { await api.post("/api/console/oidc-providers", form); setSaved(true); list.reload(); }
    catch (e) { setError((e as Error).message); }
  }

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      {error ? <Notice>{error}</Notice> : null}
      {saved ? <Notice kind="ok">Provider saved.</Notice> : null}

      <div className="panel">
        <div className="panel__head"><h2>Identity providers</h2></div>
        <div className="panel__body" style={{ padding: 0 }}>
          <table className="data">
            <thead><tr><th>Name</th><th>Issuer</th><th>Status</th><th style={{ width: 80 }} /></tr></thead>
            <tbody>
              {(list.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td>{p.display_name || p.name}</td>
                  <td className="mono small truncate" style={{ maxWidth: 260 }}>{p.issuer}</td>
                  <td><span className={`tag ${p.enabled ? "tag--live" : ""}`}>{p.enabled ? "Enabled" : "Off"}</span></td>
                  <td className="actions">
                    <button className="btn btn--danger btn--sm"
                      onClick={() => api.del(`/api/console/oidc-providers/${p.id}`).then(list.reload)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!list.data?.length ? (
                <tr><td colSpan={4} className="faint" style={{ padding: "1.25rem", textAlign: "center" }}>
                  No providers. People sign in with a username and password.
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><h2>Add a provider</h2></div>
        <div className="panel__body">
          <p className="faint small">
            Works with any OpenID Connect provider. Set the redirect URI in your provider to{" "}
            <code className="mono">{window.location.origin}/api/oidc/callback</code>.
          </p>
          <div className="field">
            <label className="field__label">Short name</label>
            <input value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} placeholder="entra" />
          </div>
          <div className="field">
            <label className="field__label">Button label</label>
            <input value={form.display_name ?? ""} onChange={(e) => set("display_name", e.target.value)}
              placeholder="Microsoft Entra ID" />
          </div>
          <div className="field">
            <label className="field__label">Issuer URL</label>
            <input className="mono" value={form.issuer ?? ""} onChange={(e) => set("issuer", e.target.value)}
              placeholder="https://login.microsoftonline.com/<tenant>/v2.0" />
            <span className="field__hint">Endpoints are discovered automatically from here.</span>
          </div>
          <div className="field">
            <label className="field__label">Client ID</label>
            <input className="mono" value={form.client_id ?? ""} onChange={(e) => set("client_id", e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Client secret</label>
            <input type="password" value={form.client_secret ?? ""} onChange={(e) => set("client_secret", e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Username claim</label>
            <input className="mono" value={form.username_claim ?? ""} onChange={(e) => set("username_claim", e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Role for new people</label>
            <select value={form.default_role_id ?? ""} onChange={(e) => set("default_role_id", e.target.value)}>
              <option value="">No role</option>
              {(roles.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field__label">Group for new people</label>
            <select value={form.default_group_id ?? ""} onChange={(e) => set("default_group_id", e.target.value)}>
              <option value="">No group</option>
              {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <label className="check">
            <input type="checkbox" checked={form.auto_register ?? true}
              onChange={(e) => set("auto_register", e.target.checked)} />
            Create an account the first time someone signs in this way
          </label>
          <button className="btn btn--primary" onClick={create} disabled={!form.name || !form.issuer}>
            Add provider
          </button>
        </div>
      </div>
    </div>
  );
}

function LdapTab() {
  const cfg = useAsync(() => api.get<LdapConfig>("/api/console/ldap"), []);
  const [form, setForm] = useState<Partial<LdapConfig> | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const v = { ...(cfg.data ?? {}), ...(form ?? {}) } as LdapConfig;
  const set = (k: keyof LdapConfig, val: unknown) => setForm({ ...v, [k]: val });

  async function save() {
    setError(""); setSaved(false);
    try { await api.post("/api/console/ldap", v); setSaved(true); cfg.reload(); setForm(null); }
    catch (e) { setError((e as Error).message); }
  }

  if (cfg.loading) return <p className="faint">Loading…</p>;

  return (
    <div className="stack" style={{ maxWidth: 680 }}>
      {error ? <Notice>{error}</Notice> : null}
      {saved ? <Notice kind="ok">LDAP settings saved.</Notice> : null}
      <div className="panel">
        <div className="panel__head"><h2>LDAP and Active Directory</h2></div>
        <div className="panel__body">
          <label className="check">
            <input type="checkbox" checked={v.enabled ?? false} onChange={(e) => set("enabled", e.target.checked)} />
            Allow people to sign in with directory credentials
          </label>
          <div className="field">
            <label className="field__label">Server URL</label>
            <input className="mono" value={v.url ?? ""} onChange={(e) => set("url", e.target.value)}
              placeholder="ldaps://dc01.example.com:636" />
          </div>
          <div className="field">
            <label className="field__label">Service account DN</label>
            <input className="mono" value={v.bindDn ?? ""} onChange={(e) => set("bindDn", e.target.value)}
              placeholder="CN=svc-rustdesk,OU=Service,DC=example,DC=com" />
            <span className="field__hint">Used to look people up. Leave blank for an anonymous search.</span>
          </div>
          <div className="field">
            <label className="field__label">Service account password</label>
            <input type="password" value={v.bindPassword ?? ""} onChange={(e) => set("bindPassword", e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Search base</label>
            <input className="mono" value={v.searchBase ?? ""} onChange={(e) => set("searchBase", e.target.value)}
              placeholder="DC=example,DC=com" />
          </div>
          <div className="field">
            <label className="field__label">Search filter</label>
            <input className="mono" value={v.searchFilter ?? ""} onChange={(e) => set("searchFilter", e.target.value)} />
            <span className="field__hint">
              <code className="mono">{"{{username}}"}</code> is replaced with what the person typed, escaped.
            </span>
          </div>
          <div className="field">
            <label className="field__label">Display name attribute</label>
            <input className="mono" value={v.displayNameAttribute ?? ""}
              onChange={(e) => set("displayNameAttribute", e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Email attribute</label>
            <input className="mono" value={v.mailAttribute ?? ""} onChange={(e) => set("mailAttribute", e.target.value)} />
          </div>
          <label className="check">
            <input type="checkbox" checked={v.autoRegister ?? true}
              onChange={(e) => set("autoRegister", e.target.checked)} />
            Create an account the first time someone signs in
          </label>
          <label className="check">
            <input type="checkbox" checked={v.tlsRejectUnauthorized ?? true}
              onChange={(e) => set("tlsRejectUnauthorized", e.target.checked)} />
            Verify the directory&rsquo;s TLS certificate
          </label>
          <button className="btn btn--primary" onClick={save} style={{ marginTop: "0.5rem" }}>Save</button>
        </div>
      </div>
    </div>
  );
}
